import { useCallback, useEffect, useRef, useState } from "react";
import { AlertTriangle, Download, Eye, Loader2, X } from "lucide-react";
import { showToast } from "./ui/custom-toast";
import { useAuthStore } from "../../stores/authStore";
import { MarkdownRenderer } from "../pages/ContentRegistry/MarkdownRenderer";
import { triggerDownload, withPdfTitle } from "../pages/ContentRegistry/utils";

/* ── Shared file preview engine ──
   Fetches a protected file URL and renders it inline. Used by the Content
   Registry preview modal and the SSP diagram preview modal, which previously
   carried two divergent copies of this logic.

   The API streams stored files back with whatever content-type was persisted,
   which is often the generic "application/octet-stream". Trusting that header
   alone made every such file (PDFs included) fall through to the "Preview not
   available" state, so the real type is detected from the leading magic bytes
   and the header / filename extension are only a fallback. */

const MAGIC_BYTES: { sig: number[]; type: string }[] = [
  { sig: [0x25, 0x50, 0x44, 0x46], type: "application/pdf" }, // %PDF
  { sig: [0x89, 0x50, 0x4e, 0x47], type: "image/png" },
  { sig: [0xff, 0xd8, 0xff], type: "image/jpeg" },
  { sig: [0x47, 0x49, 0x46, 0x38], type: "image/gif" },
  { sig: [0x42, 0x4d], type: "image/bmp" },
  { sig: [0x50, 0x4b, 0x03, 0x04], type: "application/zip" }, // docx/xlsx/pptx
  { sig: [0xd0, 0xcf, 0x11, 0xe0], type: "application/msword" }, // legacy .doc
];

function sniffBinaryType(buf: ArrayBuffer): string | null {
  const head = new Uint8Array(buf.slice(0, 16));
  for (const { sig, type } of MAGIC_BYTES) {
    if (sig.every((b, i) => head[i] === b)) return type;
  }
  // RIFF....WEBP
  const isRiff = [0x52, 0x49, 0x46, 0x46].every((b, i) => head[i] === b);
  const isWebp = [0x57, 0x45, 0x42, 0x50].every((b, i) => head[i + 8] === b);
  if (isRiff && isWebp) return "image/webp";
  return null;
}

// Control characters (other than tab/newline/carriage return) mean binary.
// Built from a string literal so no raw control bytes live in this source file.
const CONTROL_CHARS = new RegExp(
  "[\\u0000-\\u0008\\u000B\\u000C\\u000E-\\u001F]",
);

/** Decode as UTF-8 text, or null when the bytes are not readable text. */
function decodeText(buf: ArrayBuffer): string | null {
  try {
    const text = new TextDecoder("utf-8", { fatal: true }).decode(buf);
    if (CONTROL_CHARS.test(text.slice(0, 4096))) return null;
    return text;
  } catch {
    return null;
  }
}

function extensionOf(name: string): string {
  return name.toLowerCase().match(/\.([a-z0-9]+)$/)?.[1] ?? "";
}

/** Extensions that hold plain text even when the server says octet-stream. */
const TEXT_EXTENSIONS = new Set([
  "txt",
  "log",
  "json",
  "yaml",
  "yml",
  "csv",
  "tsv",
  "xml",
  "rego",
  "sql",
  "ini",
  "conf",
  "toml",
  "properties",
]);

/** Zip-based formats that are not Word documents - no inline renderer. */
const NON_DOCX_ZIP_EXTENSIONS = new Set([
  "xlsx",
  "xlsm",
  "xls",
  "pptx",
  "ppt",
  "zip",
  "jar",
  "odt",
  "ods",
  "odp",
  "epub",
]);

type PreviewKind =
  | "image"
  | "pdf"
  | "html"
  | "markdown"
  | "text"
  | "docx"
  | "doc";

/** Why a file can't be shown inline - drives the fallback copy. */
type Unsupported = { ext: string; legacyDoc: boolean };

/* ── Word renderer ──
   utils/docx (modern .docx) and utils/legacyDoc (Word 97-2003 .doc) parse the
   file with built-in browser APIs, so there is no parser library in the
   bundle. They are imported lazily all the same, keeping them off the initial
   load. */
function WordView({
  buffer,
  legacy,
  onFail,
}: {
  buffer: ArrayBuffer;
  legacy: boolean;
  onFail: () => void;
}) {
  const hostRef = useRef<HTMLDivElement>(null);
  const [rendering, setRendering] = useState(true);

  useEffect(() => {
    let cancelled = false;
    let release: (() => void) | null = null;
    const host = hostRef.current;
    setRendering(true);
    (async () => {
      const { fragment, revoke } = legacy
        ? await import("../../utils/legacyDoc").then((m) => ({
            ...m.renderLegacyDoc(buffer),
            revoke: null,
          }))
        : await import("../../utils/docx").then((m) => m.renderDocx(buffer));
      if (cancelled || !host) {
        revoke?.();
        return;
      }
      release = revoke;
      host.replaceChildren(fragment);
      setRendering(false);
    })().catch(() => {
      // Not a real Word file, or a corrupt archive - fall back to the
      // "can't preview" state instead of leaving a blank modal.
      if (!cancelled) onFail();
    });
    return () => {
      cancelled = true;
      if (release) release();
      if (host) host.replaceChildren();
    };
  }, [buffer, legacy, onFail]);

  return (
    <div className="relative w-full h-full">
      {rendering && (
        <div className="absolute inset-0 flex flex-col items-center justify-center gap-3 bg-[#f5f7fb] dark:bg-black/20">
          <Loader2 className="size-8 text-muted-foreground animate-spin" />
          <p className="text-sm text-muted-foreground">Loading preview…</p>
        </div>
      )}
      <div
        ref={hostRef}
        className="docx-view w-full h-full overflow-auto bg-white"
      />
    </div>
  );
}

/* ── Preview body: fetch, detect, render ── */
export function FilePreviewBody({
  url,
  fileName,
}: {
  url: string;
  fileName: string | null;
}) {
  const [blobUrl, setBlobUrl] = useState<string | null>(null);
  const [kind, setKind] = useState<PreviewKind | null>(null);
  const [text, setText] = useState<string | null>(null);
  const [wordBuffer, setWordBuffer] = useState<ArrayBuffer | null>(null);
  const [unsupported, setUnsupported] = useState<Unsupported | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);

  useEffect(() => {
    if (error) showToast("error", "Unable to load preview");
  }, [error]);

  useEffect(() => {
    let revoke = "";
    setLoading(true);
    setError(false);
    setBlobUrl(null);
    setKind(null);
    setText(null);
    setWordBuffer(null);
    setUnsupported(null);
    const token = useAuthStore.getState().getToken();

    // Sequential async/await keeps each step strongly typed (the previous
    // .then-chain returned a union of promises that TS couldn't narrow).
    const load = async () => {
      const res = await fetch(url, {
        credentials: "include",
        headers: token ? { Authorization: `Bearer ${token}` } : {},
      });
      if (!res.ok) throw new Error("Failed to load");
      const ct = (res.headers.get("content-type") ?? "").toLowerCase();
      // Resolve the file name from the prop or the Content-Disposition header
      // (the governance table row has no name to pass).
      const disposition = res.headers.get("content-disposition") ?? "";
      const headerFileName =
        disposition
          .match(/filename[^;=\n]*=["']?([^"';\n]+)["']?/i)?.[1]
          ?.trim() ?? "";
      const effectiveFileName = (fileName || headerFileName).toLowerCase();
      const ext = extensionOf(effectiveFileName);

      const buf = await res.arrayBuffer();
      const sniffed = sniffBinaryType(buf);

      // 1. Binary types we can render natively - trust the bytes first, then
      //    the header/extension (both lie when the API sends octet-stream).
      const isPdf =
        sniffed === "application/pdf" ||
        (!sniffed && (ct.includes("pdf") || ext === "pdf"));
      if (isPdf) {
        // Embed a /Title so Chrome's native viewer shows the real filename
        // instead of "(anonymous)" (blob: URLs carry no name).
        const withTitle = await withPdfTitle(
          new Blob([buf], { type: "application/pdf" }),
          fileName || headerFileName || null,
        );
        const objUrl = URL.createObjectURL(withTitle);
        revoke = objUrl;
        setKind("pdf");
        setBlobUrl(objUrl);
        return;
      }

      const headerImageType = ct.startsWith("image/") ? ct.split(";")[0] : null;
      const imageType = sniffed?.startsWith("image/")
        ? sniffed
        : sniffed
          ? null
          : headerImageType;
      if (imageType) {
        const objUrl = URL.createObjectURL(new Blob([buf], { type: imageType }));
        revoke = objUrl;
        setKind("image");
        setBlobUrl(objUrl);
        return;
      }

      // 2. Word documents. A .docx is a zip container and a legacy .doc is an
      //    OLE2 compound file; both have a reader in utils. Other zip-based
      //    Office formats (xlsx/pptx) have no renderer.
      if (sniffed === "application/msword") {
        setKind("doc");
        setWordBuffer(buf);
        return;
      }
      if (sniffed === "application/zip") {
        const isDocx =
          ext === "docx" ||
          ct.includes("wordprocessingml") ||
          (!NON_DOCX_ZIP_EXTENSIONS.has(ext) && !ext);
        if (isDocx) {
          setKind("docx");
          setWordBuffer(buf);
          return;
        }
        setUnsupported({ ext, legacyDoc: false });
        return;
      }

      // 3. Everything else: readable text or nothing.
      const decoded = decodeText(buf);
      if (decoded === null) {
        setUnsupported({ ext, legacyDoc: false });
        return;
      }

      // SVG is text on the wire but renders as an image.
      if (ext === "svg" || ct.includes("svg")) {
        const objUrl = URL.createObjectURL(
          new Blob([decoded], { type: "image/svg+xml" }),
        );
        revoke = objUrl;
        setKind("image");
        setBlobUrl(objUrl);
        return;
      }

      // Detect markdown by content-type, filename extension, or content pattern
      const isMd =
        ct.includes("markdown") ||
        ext === "md" ||
        ext === "mdx" ||
        (!TEXT_EXTENSIONS.has(ext) &&
          /^#{1,6} |^\*\*|^> |^```|^- \w|^\d+\. \w/.test(decoded.trimStart()));
      if (isMd) {
        setKind("markdown");
        setText(decoded);
        return;
      }

      if (ext === "html" || ext === "htm" || ct.includes("text/html")) {
        const objUrl = URL.createObjectURL(
          new Blob([decoded], { type: "text/html" }),
        );
        revoke = objUrl;
        setKind("html");
        setBlobUrl(objUrl);
        return;
      }

      // Plain text, JSON, YAML, CSV, XML, policy files: show the source.
      setKind("text");
      setText(decoded);
    };

    load()
      .catch(() => setError(true))
      .finally(() => setLoading(false));
    return () => {
      if (revoke) URL.revokeObjectURL(revoke);
    };
  }, [url, fileName]);

  // A Word file the reader can't parse (corrupt, or an encrypted legacy .doc)
  // falls back to the same message as any other unrenderable format.
  const handleWordFail = useCallback(() => {
    setKind((current) => {
      setUnsupported({
        ext: current === "doc" ? "doc" : "docx",
        legacyDoc: current === "doc",
      });
      return null;
    });
    setWordBuffer(null);
  }, []);

  const ready = !loading && !error;

  return (
    <div className="flex-1 min-h-0 overflow-auto bg-[#f5f7fb] dark:bg-black/20 flex items-center justify-center">
      {loading && (
        <div className="flex flex-col items-center gap-3 p-8">
          <Loader2 className="size-8 text-muted-foreground animate-spin" />
          <p className="text-sm text-muted-foreground">Loading preview…</p>
        </div>
      )}
      {!loading && error && (
        <div className="flex flex-col items-center gap-3 p-8 text-center">
          <AlertTriangle className="size-12 text-red-400" />
          <p className="text-sm font-medium text-foreground">
            Failed to load preview
          </p>
          <p className="text-xs text-muted-foreground">
            Use the download button to view the file.
          </p>
        </div>
      )}
      {ready && unsupported && (
        <div className="flex flex-col items-center gap-3 p-8 text-center">
          <AlertTriangle className="size-12 text-muted-foreground" />
          <p className="text-sm font-medium text-foreground">
            {unsupported.ext
              ? `Preview not available for .${unsupported.ext} files`
              : "Preview not available for this file type"}
          </p>
          <p className="text-xs text-muted-foreground">
            {unsupported.legacyDoc
              ? "Legacy Word files can't be rendered in the browser. Download the file, or upload it again in .docx format."
              : "Use the download button to view the file."}
          </p>
        </div>
      )}
      {ready && (kind === "docx" || kind === "doc") && wordBuffer && (
        <WordView
          buffer={wordBuffer}
          legacy={kind === "doc"}
          onFail={handleWordFail}
        />
      )}
      {ready && kind === "markdown" && text !== null && (
        <div className="w-full h-full overflow-auto p-6 bg-white dark:bg-[#0f0f1a]">
          <MarkdownRenderer content={text} />
        </div>
      )}
      {ready && kind === "text" && text !== null && (
        <div className="w-full h-full overflow-auto bg-white dark:bg-[#0f0f1a]">
          <pre className="m-0 p-6 font-mono text-[12px] leading-relaxed whitespace-pre-wrap break-words text-[#0f172a] dark:text-white/90">
            {text}
          </pre>
        </div>
      )}
      {ready && blobUrl && (
        <>
          {kind === "image" && (
            <img
              src={blobUrl}
              alt="Preview"
              className="max-w-full max-h-full object-contain"
            />
          )}
          {kind === "pdf" && (
            <embed src={blobUrl} type="application/pdf" className="w-full h-full" />
          )}
          {kind === "html" && (
            <iframe
              src={blobUrl}
              className="w-full h-full border-0 bg-white"
              title="Preview"
              sandbox=""
            />
          )}
        </>
      )}
    </div>
  );
}

/* ── Preview header: title, authenticated download, close ── */
export function FilePreviewHeader({
  title = "Preview",
  fileName,
  downloadUrl,
  onClose,
}: {
  title?: string;
  fileName: string | null;
  downloadUrl: string;
  onClose: () => void;
}) {
  return (
    <div className="flex items-center justify-between p-4 border-b border-border flex-shrink-0">
      <div className="flex items-center gap-2">
        <Eye className="size-4 text-indigo-500 dark:text-indigo-400" />
        <h3 className="text-sm font-bold text-foreground">
          {title}
          {fileName && (
            <span className="font-normal text-muted-foreground ml-2">
              ({fileName})
            </span>
          )}
        </h3>
      </div>
      <div className="flex items-center gap-2">
        <button
          onClick={() => triggerDownload(downloadUrl, fileName)}
          className="p-1.5 rounded-md border border-border hover:bg-accent transition-colors text-foreground"
          title="Download"
        >
          <Download className="size-4" />
        </button>
        <button
          onClick={onClose}
          className="p-1.5 rounded-md text-muted-foreground hover:text-foreground hover:bg-accent transition-colors"
        >
          <X className="size-4" />
        </button>
      </div>
    </div>
  );
}
